/**
 * Redact the storefront resume code from captured text.
 *
 * The anonymous-cart resume code rides in the URL FRAGMENT (`#r=<code>`, or
 * `r=<code>` as one of several `&`-joined hash params). A fragment never
 * reaches our server, but anything that captures `location.href` would carry
 * it off the device: Sentry events and breadcrumbs, and error toasts that echo
 * a URL. Whoever holds the code can resume that cart, so every such capture
 * point runs its text through here and gets `#r=[redacted]`.
 *
 * Only the hash part is touched: a query-string `?r=` is left alone (it is not
 * the resume code and is already visible to the server), as is `#rr=`.
 *
 * Google Analytics (index.html gtag) needs no hook: GA4's default
 * page_location excludes the fragment.
 *
 * Leaf module, no imports: main.js and the notification store both use it.
 */

export const REDACTED = "[redacted]";

// A fragment: `#` up to whitespace, a quote, or an angle bracket, so a URL
// embedded in prose or JSON is bounded.
const FRAGMENT = /#[^\s"'<>]*/g;
// `r=` at the start of the fragment or after `&`, up to the next `&`.
const RESUME_PARAM = /([#&])r=[^&]*/g;

export function redactResumeCode(value) {
    if (typeof value !== "string" || value.indexOf("#") === -1) return value;
    return value.replace(FRAGMENT, (fragment) =>
        fragment.replace(RESUME_PARAM, `$1r=${REDACTED}`),
    );
}

/**
 * Redact every string inside `value` (objects and arrays, in place). Cycle
 * safe. Returns the scrubbed value; strings come back as new strings.
 */
export function scrubResumeCodeDeep(value, seen = new WeakSet()) {
    if (typeof value === "string") return redactResumeCode(value);
    if (!value || typeof value !== "object" || seen.has(value)) return value;
    seen.add(value);
    for (const key of Object.keys(value)) {
        try {
            value[key] = scrubResumeCodeDeep(value[key], seen);
        } catch {
            // A frozen or getter-only property: leave it rather than drop
            // the whole event.
        }
    }
    return value;
}

/** Sentry `beforeSend` hook. */
export function sentryBeforeSend(event) {
    return scrubResumeCodeDeep(event);
}

/** Sentry `beforeBreadcrumb` hook. */
export function sentryBeforeBreadcrumb(breadcrumb) {
    return scrubResumeCodeDeep(breadcrumb);
}
